import React, { useState, useEffect } from 'react';
import { adminGetOverviewStats } from '../../services/blogApi';
import { StatsOverview } from '../../types/blog';

interface Props {
  secret: string;
}

const AdminStats: React.FC<Props> = ({ secret }) => {
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminGetOverviewStats(secret)
      .then((res) => setStats(res.data ?? res))
      .catch(() => setError('Failed to load stats.'))
      .finally(() => setLoading(false));
  }, [secret]);

  const CARD: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };

  const statCard = (value: string | number, label: string, sub?: string) => (
    <div style={{ ...CARD, textAlign: 'center', flex: '1 1 160px' }}>
      <p style={{ fontSize: '2rem', fontWeight: 800, background: 'linear-gradient(135deg, #0D9488 0%, #2563EB 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', margin: 0 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6b6b8a', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0.3rem 0 0' }}>
        {label}
      </p>
      {sub && <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.2rem 0 0' }}>{sub}</p>}
    </div>
  );

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: '#6b6b8a' }}>Loading stats...</div>;
  }

  if (error || !stats) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>{error || 'No stats available.'}</div>;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Top stat cards */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {statCard(stats.total_published, 'Published blogs')}
        {statCard(stats.total_views_this_week, 'Views this week')}
        {statCard(stats.total_views_this_month, 'Views this month')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* By language */}
        <div style={CARD}>
          <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>By Language</p>
          {stats.by_language.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No data yet.</p>
          )}
          {stats.by_language.map((l) => (
            <div key={l.code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', color: '#07091A' }}>{l.code}</span>
              <span style={{ fontSize: '0.85rem', color: '#6b6b8a' }}>
                {l.published} posts · {l.views_month.toLocaleString()} views/mo
              </span>
            </div>
          ))}
        </div>

        {/* Coverage gaps */}
        <div style={CARD}>
          <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>Coverage Gaps</p>
          {stats.coverage_gaps.length === 0 && (
            <p style={{ color: '#22c55e', fontSize: '0.9rem' }}>All languages covered!</p>
          )}
          {stats.coverage_gaps.map((g) => (
            <div key={g.missing_lang} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.85rem', color: '#ef4444' }}>{g.missing_lang}</span>
              <span style={{ fontSize: '0.85rem', color: '#6b6b8a' }}>{g.total_missing} missing posts</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Top posts */}
        <div style={CARD}>
          <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>Top Posts</p>
          {stats.top_posts.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No posts yet.</p>
          )}
          {stats.top_posts.map((p, i) => (
            <div key={p.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontWeight: 800, color: '#9ca3af', fontSize: '0.9rem', width: '18px', flexShrink: 0 }}>{i + 1}</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 600, color: '#07091A' }}>{p.title}</p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>{p.lang.toUpperCase()} · {p.views.toLocaleString()} views</p>
              </div>
            </div>
          ))}
        </div>

        {/* Top tags */}
        <div style={CARD}>
          <p style={{ fontWeight: 700, fontSize: '0.95rem', color: '#07091A', marginBottom: '1rem' }}>Top Tags</p>
          {stats.top_tags.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No tags yet.</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {stats.top_tags.map((tag) => (
              <span key={tag.tag} style={{ padding: '0.3rem 0.8rem', borderRadius: '999px', background: 'rgba(45,212,191,0.1)', color: '#0D9488', fontSize: '0.82rem', fontWeight: 600 }}>
                {tag.tag} <span style={{ opacity: 0.7 }}>({tag.count})</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
